import { ListBulletIcon, PlusCircleIcon, PuzzlePieceIcon } from "@heroicons/react/24/solid";
import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { LocationFormProps } from "data/reduxInterfaces";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { fetchPlaygrounds } from "redux/features/Admin/playground/playgroundSlice";
import { Checkbox, Spinner, Typography } from "@material-tailwind/react";
import { fetchEquipments } from "redux/features/Admin/equipments/equipmentSlice";
import Input from "shared/Input/Input";
import NcInputNumberOne from "components/NcInputNumber/NcInputNumberOne";

interface Playground {
  id: number;
  designation: string;
}

interface Equipment {
  id: number;
  designation: string;
}

const formSchema = z.object({
  equipment: z.array(z.string()).optional(),
  extraEquipment: z
    .array(
      z.object({
        designation: z.string(),
        price: z.number(),
        quantity: z.number(),
      })
    )
    .optional(),
  playground: z.array(z.string()).optional(),
});

const LocationPlayground: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const dispatch = useAppDispatch();

  const playgroundsStates = useAppSelector((state) => state.featurePlayground);
  const equipmentsStates = useAppSelector((state) => state.featureEquipment);

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      playground: data?.playground ?? [],
      equipment: data?.equipment ?? [],
      extraEquipment: data?.extraEquipment ?? [],
    },
    mode: "onSubmit",
  });

  const [dataLoading, setDataLoading] = useState(true);

  const [extraName, setExtraName] = useState<string>("");
  const [extraPrice, setExtraPrice] = useState<number | undefined>();
  const [extraQuantity, setExtraQuantity] = useState<number | undefined>(1);

  const [extraNameErrorMsg, setExtraNameErrorMsg] = useState<string | null>(null);
  const [extraPriceErrorMsg, setExtraPriceErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchEquipments());
    dispatch(fetchPlaygrounds());
  }, []);

  useEffect(() => {
    if (data) {
      setValue("playground", data.playground);
      setValue("equipment", data.equipment);
      setValue("extraEquipment", data.extraEquipment);
    }
  }, [data]);

  useEffect(() => {
    if (playgroundsStates.Playgrounds) {
      setDataLoading(false);
    }
  }, [playgroundsStates.Playgrounds]);

  useEffect(() => {
    if (equipmentsStates.equipments) {
      setDataLoading(false);
    }
  }, [equipmentsStates.equipments]);

  useEffect(() => {
    if (handleForm.type === "chekLocationFormFour") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const handleAddExtra = () => {
    if (!extraName) {
      setExtraNameErrorMsg("Ce champ et obligatoire");
    } else {
      setExtraNameErrorMsg(null);
    }

    if (extraPrice === undefined) {
      setExtraPriceErrorMsg("Ce champ et obligatoire");
    } else {
      setExtraPriceErrorMsg(null);
    }

    if (extraName && extraPrice !== undefined && extraQuantity !== undefined) {
      const extraValues = getValues("extraEquipment") || [];
      setValue("extraEquipment", [
        ...extraValues,
        {
          designation: extraName,
          price: extraPrice,
          quantity: extraQuantity,
        },
      ]);
      trigger("extraEquipment");

      setExtraName("");
      setExtraPrice(undefined);
      setExtraQuantity(1);
    }
  };

  const handleSelectEquipment = (equipmentId: string, isChecked: boolean) => {
    const updatedEquipment = isChecked
      ? [...(getValues("equipment") ?? []), equipmentId]
      : getValues("equipment")?.filter((equipment: string) => equipment !== equipmentId);

    setValue("equipment", updatedEquipment);
    trigger("equipment");
  };

  const handleRemoveExtra = (index: number) => {
    const extraEquipment = [...(getValues("extraEquipment") || [])];
    extraEquipment.splice(index, 1);
    setValue("extraEquipment", extraEquipment);
    trigger("extraEquipment");
  };

  const handleSelectPlayground = (playgroundDesignation: string, isChecked: boolean) => {
    const updatedPlaygrounds = isChecked
      ? [...(getValues("playground") ?? []), playgroundDesignation]
      : getValues("playground")?.filter((playground: string) => playground !== playgroundDesignation);

    setValue("playground", updatedPlaygrounds);
    trigger("playground");
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header moderne */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6 mb-8 border border-purple-100 dark:border-purple-800/50">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
              <PuzzlePieceIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Aires de jeux et activités</h3>
              <p className="text-purple-600 dark:text-purple-400 text-sm">
                Enrichissez l'expérience de vos visiteurs
              </p>
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            Sélectionnez les aires de jeux et activités disponibles dans votre espace pour attirer plus de familles
            et créer des expériences mémorables.
          </p>
        </div>

        {/* Aires de jeux modernes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <PuzzlePieceIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Aires de jeux disponibles</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">Choisissez les activités proposées</p>
              </div>
            </div>
            {playgroundsStates.Playgrounds.length > 0 && (
              <div className="text-sm text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded-full">
                {(getValues("playground") || []).length} / {playgroundsStates.Playgrounds.length} sélectionnées
              </div>
            )}
          </div>

          {dataLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Spinner color="purple" className="w-8 h-8 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Chargement des aires de jeux...</p>
            </div>
          ) : playgroundsStates.Playgrounds.length === 0 ? (
            <div className="text-center py-12">
              <PuzzlePieceIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
                Aucune aire de jeux disponible
              </h4>
              <p className="text-gray-400 dark:text-gray-500">Les aires de jeux seront ajoutées prochainement.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {playgroundsStates.Playgrounds?.map((playground: Playground) => {
                const playgroundValues = getValues("playground") || [];
                const isSelected = playgroundValues.includes(playground.designation);
                return (
                  <div
                    key={playground.id}
                    className={`
                      relative p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer hover:shadow-md
                      ${
                        isSelected
                          ? "border-purple-400 bg-purple-50 dark:bg-purple-900/20 shadow-lg transform scale-105"
                          : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:border-purple-200 dark:hover:border-purple-700"
                      }
                    `}
                    onClick={() => handleSelectPlayground(playground.designation, !isSelected)}
                  >
                    {/* Badge de sélection */}
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}

                    {/* Contenu de la carte */}
                    <div className="flex items-start space-x-3">
                      <div
                        className={`
                          w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 mt-1
                          ${
                            isSelected
                              ? "bg-purple-500 text-white"
                              : "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                          }
                        `}
                      >
                        <PuzzlePieceIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5
                          className={`
                            font-medium leading-tight break-words transition-colors
                            ${
                              isSelected
                                ? "text-purple-800 dark:text-purple-200"
                                : "text-gray-700 dark:text-gray-300"
                            }
                          `}
                          title={playground.designation}
                        >
                          {playground.designation}
                        </h5>
                        <p
                          className={`
                            text-xs leading-tight mt-1 transition-colors
                            ${
                              isSelected
                                ? "text-purple-600 dark:text-purple-400"
                                : "text-gray-500 dark:text-gray-400"
                            }
                          `}
                        >
                          Activité disponible
                        </p>
                      </div>
                    </div>

                    {/* Checkbox caché pour l'accessibilité */}
                    <Checkbox
                      name={playground.designation}
                      checked={isSelected}
                      onChange={(e) => handleSelectPlayground(playground.designation, e.target.checked)}
                      disabled={isLoading}
                      className="sr-only"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Résumé des sélections */}
          {(() => {
            const selectedPlaygrounds = getValues("playground") || [];
            return (
              selectedPlaygrounds.length > 0 && (
                <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <h5 className="text-sm font-medium text-purple-800 dark:text-purple-200 mb-2">
                    Aires de jeux sélectionnées ({selectedPlaygrounds.length})
                  </h5>
                  <div className="flex flex-wrap gap-2">
                    {selectedPlaygrounds.map((playgroundName: string, index: number) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-800/50 text-purple-800 dark:text-purple-200"
                      >
                        {playgroundName}
                      </span>
                    ))}
                  </div>
                </div>
              )
            );
          })()}
        </div>
      </form>
    </Fragment>
  );
};

export default LocationPlayground;
